import React, { useCallback, useEffect, useMemo } from 'react'
import { Form, Row, Spin } from 'antd'
import { isEmpty } from 'lodash'

import {
  DirtyPromptForm,
  FormButtons,
  FormImageUpload,
} from '../../../common/components/sections'
import { PERMISSIONS } from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'

import { useUpdateAllergenImageDetailsMutation } from '../../hooks'
import { allergenPropType } from '../../prop-types'

import { allergenImageFormInitialValues } from './initial-values'

const AllergenImageDetailsForm = ({ allergen }) => {
  const [form] = Form.useForm()

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.ALLERGEN.UPDATE_IMAGE_DETAILS,
  ])

  const [
    updateAllergenImageDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateAllergenImageDetailsMutation()

  const initialValues = useMemo(
    () => allergenImageFormInitialValues(allergen),
    [allergen],
  )

  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const handleSaveData = useCallback(
    data => updateAllergenImageDetails(allergen.id, data),
    [allergen.id, updateAllergenImageDetails],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Allergen details error',
        description: saveError,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
  }, [allergen.imageUrl, resetForm])

  return isSaveLoading || isEmpty(initialValues) ? (
    <Spin />
  ) : (
    <>
      <DirtyPromptForm
        isDirty={isDirty}
        form={form}
        initialValues={initialValues}
        name="allergenImageForm"
        onFinish={handleSaveData}
        autoComplete="off"
        onFieldsChange={setToDirty}
      >
        <Row>
          <FormImageUpload
            disabled={!isFormEnabled}
            labelAbove
            name="image"
            label="Image"
          />
        </Row>
        <FormButtons
          permissions={[PERMISSIONS.ALLERGEN.UPDATE_IMAGE_DETAILS]}
          isDisabled={!isDirty}
          onReset={resetForm}
        />
      </DirtyPromptForm>
    </>
  )
}

AllergenImageDetailsForm.propTypes = {
  allergen: allergenPropType.isRequired,
}

export default AllergenImageDetailsForm
